import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { fetchAuthenticatedDocumentBlob, resolveDocumentUrl } from "../api/documents.api";
import Button from "../ui/Button";
import ModalA11yWrapper from "../ui/ModalA11yWrapper";
import { colors, radius, shadows, spacing, typography } from "../ui/tokens";

const pdfNamePattern = /\.pdf$/i;
const imageNamePattern = /\.(png|jpe?g|gif|webp)$/i;
const textNamePattern = /\.(txt|log|md|csv)$/i;

const getPreviewKind = ({ filename, mimeType }) => {
  const normalizedMime = String(mimeType || "").toLowerCase();
  const normalizedName = String(filename || "");
  if (normalizedMime === "application/pdf" || pdfNamePattern.test(normalizedName)) {
    return "pdf";
  }
  if (normalizedMime.startsWith("image/") || imageNamePattern.test(normalizedName)) {
    return "image";
  }
  if (normalizedMime === "text/plain" || textNamePattern.test(normalizedName)) {
    return "text";
  }
  return "unsupported";
};

const formatBytes = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const readBlobText = async (blob) => {
  if (!blob) return "";
  if (typeof blob.text === "function") {
    return blob.text();
  }
  if (typeof Response !== "undefined") {
    return new Response(blob).text();
  }
  return "";
};

const shellStyle = {
  display: "grid",
  gridTemplateRows: "auto 1fr auto",
  height: "100%",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.lg,
  padding: spacing.xl,
  borderBottom: `1px solid ${colors.border}`,
  background: "linear-gradient(135deg, rgba(15, 118, 110, 0.12) 0%, rgba(8, 145, 178, 0.06) 100%)",
};

const bodyStyle = {
  background: "#e2e8f0",
  padding: spacing.lg,
  overflow: "auto",
};

const previewFrameStyle = {
  minHeight: 520,
  height: "100%",
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.xl,
  boxShadow: shadows.sm,
  overflow: "hidden",
};

const footerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
  padding: spacing.lg,
  borderTop: `1px solid ${colors.border}`,
  background: colors.surface,
};

export default function EvidenceDocumentViewer({
  open,
  onClose,
  title,
  filename,
  mimeType,
  sizeBytes,
  downloadUrl,
}) {
  const titleId = useId();
  const closeRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [textContent, setTextContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const previewKind = useMemo(() => getPreviewKind({ filename, mimeType }), [filename, mimeType]);
  const resolvedDownloadUrl = useMemo(() => resolveDocumentUrl(downloadUrl), [downloadUrl]);

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    setPreviewUrl(null);
    setTextContent("");
    setPreviewError("");
    setLoading(false);

    if (!open || !downloadUrl || previewKind === "unsupported") {
      return () => {
        active = false;
      };
    }

    setLoading(true);
    fetchAuthenticatedDocumentBlob(downloadUrl)
      .then(async (blob) => {
        if (!active) return;
        if (previewKind === "text") {
          const text = await readBlobText(blob);
          if (!active) return;
          setTextContent(text);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      })
      .catch((error) => {
        if (active) {
          setPreviewError(error?.message || "Preview unavailable.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [downloadUrl, open, previewKind]);

  if (!open) return null;

  const handleDownload = () => {
    if (!resolvedDownloadUrl) return;
    window.open(resolvedDownloadUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenInNewTab = () => {
    const target = previewUrl || resolvedDownloadUrl;
    if (!target) return;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  return (
    <ModalA11yWrapper
      open={open}
      onClose={onClose}
      labelledBy={titleId}
      initialFocusRef={closeRef}
      backdropStyle={{ background: "rgba(15, 23, 42, 0.48)", zIndex: 3200 }}
      containerStyle={{
        width: "min(94vw, 1120px)",
        height: "min(92vh, 860px)",
        padding: 0,
        overflow: "hidden",
        borderRadius: 20,
      }}
    >
      <div style={shellStyle} data-testid="evidence-document-viewer">
        <div style={headerStyle}>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                marginBottom: spacing.xs,
                color: colors.brandPrimary,
                fontSize: typography.size.xs,
                fontWeight: typography.weight.bold,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              Document preview
            </div>
            <h2
              id={titleId}
              style={{
                margin: 0,
                color: colors.text,
                fontSize: typography.size["2xl"],
                fontWeight: typography.weight.bold,
                lineHeight: typography.lineHeight.compact,
                wordBreak: "break-word",
              }}
            >
              {title || filename || "Document"}
            </h2>
            <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap", marginTop: spacing.sm }}>
              {filename ? (
                <span style={{ color: colors.textSecondary, fontSize: typography.size.xs }}>{filename}</span>
              ) : null}
              {mimeType ? (
                <span style={{ color: colors.textSecondary, fontSize: typography.size.xs }}>{mimeType}</span>
              ) : null}
              {sizeBytes ? (
                <span style={{ color: colors.textSecondary, fontSize: typography.size.xs }}>{formatBytes(sizeBytes)}</span>
              ) : null}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            ref={closeRef}
            aria-label="Close document viewer"
            style={{ minWidth: 42, minHeight: 42, padding: 0 }}
          >
            X
          </Button>
        </div>

        <div style={bodyStyle}>
          <div style={previewFrameStyle}>
            {loading ? (
              <div style={{ padding: spacing.xl, color: colors.textSecondary }}>Loading document preview...</div>
            ) : null}
            {!loading && previewError ? (
              <div style={{ padding: spacing.xl, color: colors.danger }}>{previewError}</div>
            ) : null}
            {!loading && !previewError && previewKind === "pdf" && previewUrl ? (
              <iframe
                title={title || filename || "Document preview"}
                src={previewUrl}
                style={{ width: "100%", height: "100%", minHeight: 520, border: "none" }}
              />
            ) : null}
            {!loading && !previewError && previewKind === "image" && previewUrl ? (
              <div
                style={{
                  minHeight: 520,
                  display: "grid",
                  placeItems: "center",
                  background: "radial-gradient(circle at top, rgba(191,219,254,0.28), transparent 38%), #f8fafc",
                }}
              >
                <img
                  alt={title || filename || "Document preview"}
                  src={previewUrl}
                  style={{ maxWidth: "100%", maxHeight: "78vh", objectFit: "contain" }}
                />
              </div>
            ) : null}
            {!loading && !previewError && previewKind === "text" ? (
              <pre
                style={{
                  margin: 0,
                  minHeight: 520,
                  padding: spacing.xl,
                  color: colors.text,
                  background: "#f8fafc",
                  fontSize: typography.size.sm,
                  lineHeight: typography.lineHeight.reading,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {textContent || "This text document is empty."}
              </pre>
            ) : null}
            {!loading && !previewError && previewKind === "unsupported" ? (
              <div
                style={{
                  minHeight: 520,
                  display: "grid",
                  placeItems: "center",
                  padding: spacing.xl,
                  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                }}
              >
                <div style={{ maxWidth: 520, textAlign: "center", display: "grid", gap: spacing.md }}>
                  <div style={{ color: colors.text, fontSize: typography.size.xl, fontWeight: typography.weight.bold }}>
                    Preview isn't available for this file type.
                  </div>
                  <div style={{ color: colors.textSecondary, lineHeight: typography.lineHeight.reading }}>
                    Download the file to open it in the appropriate desktop application.
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div style={footerStyle}>
          <div style={{ color: colors.textSecondary, fontSize: typography.size.xs }}>
            {previewKind === "unsupported" ? "Download remains available." : "Close the viewer to return to your current page."}
          </div>
          <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
            {downloadUrl ? (
              <Button type="button" variant="secondary" onClick={handleDownload}>
                Download
              </Button>
            ) : null}
            {(previewUrl || resolvedDownloadUrl) && previewKind !== "text" ? (
              <Button type="button" variant="ghost" onClick={handleOpenInNewTab}>
                Open in new tab
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </ModalA11yWrapper>
  );
}
